
import { NavbarItem } from './models/navbar-item.model';
import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { AuthService } from 'src/app/services/auth.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { IsRouteActivePipe } from 'src/app/pipes/is-route-active.pipe';
import { NavigationService } from '../services/navigation.service';
import { Subscription } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    standalone: false
})
export class NavbarComponent implements OnInit {

	/**
	 * Sets expanded state for each `NavbarItem` passed depending on whether it maches active route.
	 * @param {NavbarItem[]} items Items of navbar.
	 */
	private setExpandedState(items: NavbarItem[]): void {

		for (let a = 0; a < items.length; a++) {
			if (this._isRouteActivePipe.isActive(items[a].path)) {
				items[a].expanded = true;
			}

			this.setExpandedState(items[a].children);
		}
	}

	private _isRouteActivePipeSubscriptions: Subscription[] = [];

	/**
	 * Subscribes `IsRouteActivePipe` to set expanded state for each `NavbarItem` passed depending on whether it maches active route.
	 * Uses `IsRouteActivePipe` to determine if path that `NavbarItem` has is actually an active route.
	 * Unsubscribes from old `SsRouteActivePipe` subscriptions.
	 * Useful for cases when `node.path` changes its value or for initial expanded state.
	 * @param {NavbarItem[]} items Items of navbar.
	 */
	private subscribeToAutomaticExpandedStateChange(items: NavbarItem[], unsubscribe: boolean = true): void {

		if (unsubscribe) {
			for (let a = 0; a < this._isRouteActivePipeSubscriptions.length; a++) {
				this._isRouteActivePipeSubscriptions[a].unsubscribe();
			}

			this._isRouteActivePipeSubscriptions = [];
		}

		for (let a = 0; a < items.length; a++) {
			const subscription: Subscription = this._isRouteActivePipe.transform(items[a].path).subscribe(
				{
					next: (isRouteActive) => {
						items[a].expanded = (items[a].expanded || isRouteActive) && !this._collapsed;
					}
				}
			);

			this._isRouteActivePipeSubscriptions.push(subscription);

			this.subscribeToAutomaticExpandedStateChange(items[a].children, false);
		}
	};

	protected _defaultStateIsCollapsed: boolean;
	public get defaultStateIsCollapsed(): boolean {
		return this._defaultStateIsCollapsed;
	}

	private _localStorageService: LocalStorageService = inject(LocalStorageService);

	protected _collapsed: boolean = false;
	public get collapsed(): boolean {
		return this._collapsed;
	}

	/**
	 * Switch to specified view state that is either collapsed or expanded.
	 * @param {boolean} collapsed State of the navbar.
	 */
	public switchToViewState(collapsed: boolean): void {
		this._collapsed = collapsed;

		this._localStorageService.set<boolean>(
			`perito_navbarCollapsed`,
			this._collapsed
		);

		if (this._collapsed) {
			const resetExpandedState = (items: NavbarItem[]) => {
				if (items != undefined && this._items != undefined) {
					for (let a = 0; a < items.length; a++) {
						if (this._items[a] != undefined) {
							this._items[a].expanded = false;
							resetExpandedState(items[a].children);
						}
					}
				}
			};

			resetExpandedState(this._items);
		} else {
			this.setExpandedState(this._items);
		}
	}

	/**
	 * Switch to default view state that is either collapsed or expanded.
	 * Does nothing if the view state is already in default state.
	 */
	public switchToDefaultViewState(): void {
		if (this._collapsed !== this._defaultStateIsCollapsed) {
			this.switchToViewState(this._defaultStateIsCollapsed)
		}
	}

	/**
	 * Change current view state to the opposite.
	 * If it was collapsed change to expanded and viseversa.
	 */
	public toggleViewState(): void {
		this.switchToViewState(!this._collapsed);

		this._defaultStateIsCollapsed = this._collapsed;
	}

	private _items: NavbarItem[];
	public get items(): NavbarItem[] {
		return this._items;
	}
	@Input()
	public set items(value: NavbarItem[]) {
		this._items = value;

		for (let a = 0; a < this._items.length; a++) {
			const item = this._items[a];

			item.expanded = this._collapsed;
		}

		this.subscribeToAutomaticExpandedStateChange(this._items);
	}

	@Output()
	public selectedItemChanged = new EventEmitter<NavbarItem>();

	/**
	 * Note: `_selectedItem` and hightlited item in the navbar are not always the same.
	 */
	private _selectedItem: NavbarItem;
	public get selectedItem(): NavbarItem {
		return this._selectedItem;
	}
	public set selectedItem(value: NavbarItem) {
		this._selectedItem = value;

		this.selectedItemChanged.emit(this._selectedItem);
	}

	/**
	 * Executed when user clicks on element of a navbar which in turn assigns a `selectedItem`.
	 * Executes node `onClick` event if developer/user has explicitly defined such event for a node.
	 * @param node Element of a navbar, either expandable menu item or navigational item.
	 */
	public onNavigationClick(node: NavbarItem): void {
		node.onClick();

		this.selectedItem = node;
	}

	constructor(
		private _isRouteActivePipe: IsRouteActivePipe,
		private router: Router,
		// private _localStorageService: LocalStorageService,
		public authService: AuthService,
		public navigationService: NavigationService,
	) {
		this._collapsed = this._localStorageService.get<boolean>(`perito_navbarCollapsed`);
	}

	public applicationTitle: string = "";
	public applicationColor: string = "";
	hideNav: boolean = false;
	ngOnInit(): void {
		this._defaultStateIsCollapsed = this._collapsed;

		this.authService.castApplicationTitle.subscribe((applicationTitle) => this.applicationTitle = applicationTitle);
		this.authService.castApplicationColor.subscribe(
			(applicationColor) => {
				this.applicationColor = applicationColor;

				const docStyle = document.documentElement.style;
				docStyle.setProperty('--background-color-dynamic', applicationColor);
			}
		);

		this.router.events.subscribe(event => {
			if (event instanceof NavigationEnd) {
			  const urlTree = this.router.parseUrl(this.router.url);
			  this.hideNav = urlTree.queryParams['nav'] === 'disable';
			}
		  });
	}
	
	currentYear: number = new Date().getFullYear();
}
